import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import type { Ref } from 'vue';
import { computed, nextTick, ref, watch } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { useState } from '@/composable/useState';
import { assignArray, remove, upsert } from '@/core/lib/array';
import { isInvalidLiveMp1X2Odds, isValidOdds } from '@/core/lib/oddsHelper';
import { deepFreeze } from '@/core/lib/utils';
import { useOddsToken } from '@/core/oddsApi/composable/useOddsToken';
import { formatOdds } from '@/core/oddsApi/helpers/formatOdds';
import type {
    EnumPresetFilterType,
} from '@/core/oddsApi/oddsApiType';
import {
    EnumOddsCategoryType,
    useOddsQuery,
    useOddsSubscription,
} from '@/core/oddsApi/oddsApiType';
import type { IOdds } from '@/interface/IOdds';
import type { BetTypeGroup, LeagueSize, SportType } from '@/interface/enum';
import { MarketType, PriceStyle } from '@/interface/enum';
import { useBehaviorStore } from '@/store/behaviorStore';

export function useEventOdds({
    eventId,
    oddsId,
    presetFilter,
    oddsCategory,
    priceStyle,
    marketGroupIds,
    excludeMarketGroupIds,
    isActive = ref(true),
    countBehavior = false,
    sportType,
    leagueSize,
    isSimulation,
    marketTypes,
}: {
    eventId: number;
    oddsId?: number;
    presetFilter: EnumPresetFilterType;
    oddsCategory: EnumOddsCategoryType;
    priceStyle: MaybeRef<PriceStyle>;
    marketGroupIds?: MaybeRef<BetTypeGroup[] | null>;
    excludeMarketGroupIds?: MaybeRef<BetTypeGroup[] | null>;
    isActive?: Ref<boolean>;
    countBehavior?: boolean;
    sportType: SportType;
    leagueSize: LeagueSize;
    isSimulation: boolean;
    marketTypes?: MaybeRef<MarketType[] | null>;
}) {
    // sort here to generate a stable query
    const sortedMarketGroupIds = computed(() => {
        const _marketGroupIds = get(marketGroupIds);
        return _marketGroupIds ? _marketGroupIds.sort((a, b) => a - b) : null;
    });

    const sortedExcludeMarketGroupIds = computed(() => {
        const _excludeMarketGroupIds = get(excludeMarketGroupIds);
        return _excludeMarketGroupIds ? _excludeMarketGroupIds.sort((a, b) => a - b) : null;
    });

    const { getOddsToken } = useOddsToken();

    const variables = computed(() => {
        const oddsToken = getOddsToken(sportType, leagueSize, isSimulation);

        return {
            query: {
                id: eventId,
                oddsId,
                filter: presetFilter,
                marketGroupIds: sortedMarketGroupIds.value,
                excludeMarketGroupIds: sortedExcludeMarketGroupIds.value,
                oddsCategory,
                priceStyle: PriceStyle.toOddsApi(get(priceStyle)),
                oddsToken,
                marketTypes: get(marketTypes)?.map(marketType => MarketType.toOddsApi(marketType)),
            },
        };
    });

    const [isEnabled, setIsEnabled] = useState(true);
    const refetch = async () => {
        if (isEnabled.value) {
            setIsEnabled(false);
            nextTick(() => setIsEnabled(true));
        }
    };

    const { loaded, loading, onResult, error } = useOddsQuery(
        variables,
        deepCompareComputed(() => ({
            enabled: isActive.value && isEnabled.value && !!variables.value.query.oddsToken,
        })),
    );
    const { onSubscription } = useOddsSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: isActive.value && !!variables.value.query.oddsToken,
        })),
    );

    const _oddsList = ref<IOdds[]>([]);
    const oddsList = computed(() => {
        const isMixParlayMode = oddsCategory === EnumOddsCategoryType.MixParlay;
        return _oddsList.value.filter(odds => isValidOdds(odds) && !isInvalidLiveMp1X2Odds(odds, isMixParlayMode));
    });

    const { addOddsQueryPerEventCount } = useBehaviorStore();
    onResult((result) => {
        const newOddsList = (result ?? [])
            .filter(odds => odds.eventResult?.marketGroup)
            .map(odds => deepFreeze(formatOdds(odds)));
        assignArray(_oddsList.value, newOddsList, item => item.id);

        if (countBehavior) {
            addOddsQueryPerEventCount(eventId);
        }
    });

    onSubscription((subscription) => {
        const toUpdate = (subscription?.updated ?? [])
            .filter(x => x.eventResult?.marketGroup)
            .map(x => deepFreeze(formatOdds(x)));

        upsert(_oddsList.value, toUpdate, item => item.id);
        remove(_oddsList.value, subscription?.deleted ?? [], (a, b) => a.id === b);
    });

    watch(isActive, (value) => {
        if (!value) {
            loaded.value = false;
        }
    });

    return {
        loaded,
        loading,
        refetch,
        oddsList,
        error,
    };
}
